import { useEffect, useState } from "react";
import { ChevronDown, Compass, Quote } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import LoadFailureNote from "./LoadFailureNote";
import LoadFailedCard from "./LoadFailedCard";
import api from "../lib/api";
import { COACH_NOTHING_SHARED, COACH_OWN_WORDS_NOTICE, displayItems, presentSections } from "../lib/identitySharing";
import SharedIdentityView from "./SharedIdentityView";

/**
 * The coach's read of a linked player's Identity & Vision (issue #292 slice
 * 1), on the coach portal.
 *
 * ============================================================
 * IT SHOWS WHAT WAS SHARED. IT SHOWS NOTHING ELSE — INCLUDING
 * THE FACT THAT SOMETHING ELSE EXISTS.
 * ============================================================
 * The API omits a section when the player has not shared it AND when they
 * have shared it but written nothing, so an absence is deliberately
 * ambiguous (`api/src/routes/identitySharing.ts`). This component must not
 * hand that distinction back. There is no greyed-out "Strengths &
 * Weaknesses — not shared" row, no section list with ticks, and no count.
 * A placeholder for a withheld section would tell a coach exactly what the
 * response shape refuses to say: that the player has writing they chose not
 * to show them.
 *
 * `COACH_NOTHING_SHARED` is worded under the same constraint — "nothing to
 * show", never "the player has chosen not to share", because this screen
 * cannot know that a choice was made.
 *
 * ============================================================
 * MARKED AS THE PLAYER'S OWN WORDS, AND READ-ONLY
 * ============================================================
 * #292 work item 4. The heading says whose writing it is and the notice
 * says the coach cannot change it — which is true at the API, where no
 * coach write path exists at all. There is deliberately no comment box:
 * *"no coach comment inside the section — feedback already has a home"*.
 * A coach with something to say uses Feedback, which the player knows how
 * to read.
 *
 * ============================================================
 * A TAB, NOT AN ALWAYS-OPEN CARD — THE OPPOSITE OF #287
 * ============================================================
 * `PlayerHealthPanel` sits outside the tab strip because a coach who has
 * to remember to look for an allergy finds out about it afterwards. The
 * argument does not transfer: nothing here is needed in a hurry, and the
 * counter-argument #287 weighed and lost — a person's private writing open
 * on a phone screen at a public ground — wins on this record. It renders
 * inside the portal's existing tab strip.
 *
 * ============================================================
 * A FAILED LOAD IS NOT "NOTHING SHARED"
 * ============================================================
 * #271. A 404 IS a legitimate answer here (it means "nothing to show", by
 * design), so this component distinguishes it from a transport failure: a
 * 404 renders the empty state, anything else renders `LoadFailureNote`. An
 * empty panel after a dropped connection would tell a coach the player has
 * shared nothing, which is a claim about a person made by a network error.
 */
export default function PlayerIdentityPanel({ playerId, playerName, viewerRole, variant }) {
  const [sections, setSections] = useState([]);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isCoach = viewerRole === "coach";

  const load = () => {
    if (!playerId || !isCoach) {
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    setFailed(false);
    return api
      .get(`/players/${playerId}/identity`)
      .then((r) => {
        setSections(presentSections(r.data));
        setLoading(false);
      })
      .catch((e) => {
        // 404 is the designed answer for "nothing to show you" — it is
        // byte-identical to "you have no access", and neither is an error
        // worth alarming a coach about. Anything else genuinely failed.
        const status = e?.response?.status;
        setSections([]);
        setFailed(status !== 404);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, viewerRole]);

  if (variant === "coach-player") {
    return (
      <CoachPlayerIdentityCard
        playerName={playerName}
        isCoach={isCoach}
        loading={loading}
        failed={failed}
        sections={sections}
        onRetry={load}
      />
    );
  }

  return (
    <Card className="border-slate-200" data-testid="cp-identity-panel">
      <CardContent className="p-4 sm:p-5 space-y-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#0B2E5915" }}
          >
            <Compass size={16} color="#0B2E59" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-base text-slate-900 break-words">
              {playerName ? `${playerName}'s own words` : "The player's own words"}
            </h3>
            <p className="text-xs text-slate-600 break-words">Identity &amp; Vision, shared with you by the player.</p>
          </div>
        </div>

        {!isCoach && (
          <p className="text-sm text-slate-600 break-words" data-testid="cp-identity-not-for-admin">
            Identity &amp; Vision is shown to the coach the player shares it with. Platform admins don't see it.
          </p>
        )}

        {isCoach && failed && (
          <LoadFailureNote what="this player's Identity &amp; Vision" onRetry={load} testId="cp-identity-load-failed" />
        )}

        {isCoach && !failed && loading && (
          <p className="text-sm text-slate-500" data-testid="cp-identity-loading">
            Loading…
          </p>
        )}

        {isCoach && !failed && !loading && (
          <>
            {sections.length === 0 ? (
              <p className="text-sm text-slate-700 break-words" data-testid="cp-identity-nothing-shared">
                {COACH_NOTHING_SHARED}
              </p>
            ) : (
              <>
                {/* Issue #460 — the section cards. The wall this replaced
                    rendered every section flat in one card: tiny grey
                    labels over body paragraphs, full-width unreadable lines,
                    list answers as comma run-ons. `SharedIdentityView` is a
                    reusable component (the #497 anti-fork) so #508's
                    `PlayerProfileView` renders the identical layout; the
                    `cp-identity-<section>` testids journey 13 asserts on are
                    preserved through its default `testIdPrefix`. */}
                <SharedIdentityView sections={sections} testIdPrefix="cp-identity" />

                <div className="flex items-start gap-2 pt-1 min-w-0">
                  <Quote size={14} color="#0B2E59" className="mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-600 break-words" data-testid="cp-identity-own-words">
                    {COACH_OWN_WORDS_NOTICE}
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 2 (D240, D251) — the coach's player page, `CoachPlayerGoals.dc.html`.
//
// A short card first: "Identity · in [name]'s words", the read-only line and
// three answers (Why play, Top ambitions, Greatest strengths) of two lines each,
// then "Read all of it", which opens the round-5 card IN PLACE of the short one
// (a fold that changes the view where it is, not a place to go: D224). The load,
// the 404-is-not-an-error rule and the non-oracle rule above are untouched: the
// short card draws an answer only if the player shared it and wrote it, and a
// section the payload omits leaves no heading, no row and no "not shared".
// `variant` is unset on the flag-off page, which keeps the shipped card above.
// ---------------------------------------------------------------------------

const KIT_FRAME = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 14,
  minWidth: 0,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

/** The three answers the short card leads with, grouped by the section they come from. */
const SHORT_ANSWERS = [
  { section: "vision", rows: [["why_play", "Why play"], ["top_ambitions", "Top ambitions"]] },
  { section: "strengths_weaknesses", rows: [["greatest_strengths", "Greatest strengths"]] },
];

function shortAnswersOf(sections) {
  return SHORT_ANSWERS.map(({ section, rows }) => {
    const content = sections.find((s) => s.key === section)?.content || {};
    return {
      section,
      rows: rows
        .map(([field, label]) => ({ field, label, value: displayItems(content[field]).join(", ") }))
        .filter((r) => r.value !== ""),
    };
  }).filter((g) => g.rows.length > 0);
}

function IdentityHeader({ title, line }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
      <div
        style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 10, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Compass size={16} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>{title}</div>
        <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{line}</div>
      </div>
    </div>
  );
}

function CoachPlayerIdentityCard({ playerName, isCoach, loading, failed, sections, onRetry }) {
  const [open, setOpen] = useState(false);
  const ownWords = playerName ? `${playerName}'s own words` : "The player's own words";
  const fullHeader = <IdentityHeader title={ownWords} line="Identity & Vision, shared with you by the player." />;

  if (!isCoach) {
    return (
      <div style={KIT_FRAME} data-testid="cp-identity-panel">
        {fullHeader}
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="cp-identity-not-for-admin">
          Identity &amp; Vision is shown to the coach the player shares it with. Platform admins don't see it.
        </div>
      </div>
    );
  }

  if (failed) {
    return (
      <div style={KIT_FRAME} data-testid="cp-identity-panel">
        {fullHeader}
        <LoadFailedCard title="Couldn't load this player's Identity & Vision" onRetry={onRetry} testId="cp-identity-load-failed" />
      </div>
    );
  }

  if (loading) {
    const bar = (w, h) => ({ height: h, width: w, borderRadius: 6, background: "var(--nw-line)" });
    return (
      <div style={KIT_FRAME} data-testid="cp-identity-panel">
        {fullHeader}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="cp-identity-loading" aria-busy="true">
          <div style={bar("30%", 10)} />
          <div style={bar("90%", 14)} />
          <div style={bar("70%", 14)} />
          <div style={bar("30%", 10)} />
          <div style={bar("80%", 14)} />
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div style={KIT_FRAME} data-testid="cp-identity-panel">
        {fullHeader}
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", overflowWrap: "anywhere" }} data-testid="cp-identity-nothing-shared">
          {COACH_NOTHING_SHARED}
        </div>
      </div>
    );
  }

  const groups = shortAnswersOf(sections);
  // Nothing among the three answers to lead with: there is no short card to
  // draw, so the whole card shows and has nothing to fold.
  const foldable = groups.length > 0;
  const showFull = open || !foldable;

  const toggle = foldable && (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={showFull ? "true" : "false"}
      data-testid="cp-identity-read-all"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        minHeight: 44,
        margin: "0 -14px -14px",
        padding: 0,
        background: "transparent",
        border: 0,
        borderTop: "1px solid var(--nw-line)",
        fontSize: 14,
        fontWeight: 600,
        color: "var(--nw-navy)",
        cursor: "pointer",
      }}
    >
      {showFull ? "Show less" : "Read all of it"}
      <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" style={{ transform: showFull ? "rotate(180deg)" : "none" }} />
    </button>
  );

  if (showFull) {
    return (
      <div style={KIT_FRAME} data-testid="cp-identity-panel">
        {fullHeader}
        <SharedIdentityView sections={sections} testIdPrefix="cp-identity" appearance="r5" />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
          <Quote size={14} color="var(--nw-navy)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-lede)", overflowWrap: "anywhere" }} data-testid="cp-identity-own-words">
            {COACH_OWN_WORDS_NOTICE}
          </div>
        </div>
        {toggle}
      </div>
    );
  }

  return (
    <div style={KIT_FRAME} data-testid="cp-identity-panel">
      <IdentityHeader
        title={playerName ? `Identity · in ${playerName}'s words` : "Identity · in the player's words"}
        line="Shared with you by the player. You can read it; you can't change it."
      />
      <div
        style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}
      >
        {groups.map((g) => (
          <div key={g.section} data-testid={`cp-identity-${g.section}`} style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            {g.rows.map((r) => (
              <div key={r.field} style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>{r.label}</div>
                <div
                  className="line-clamp-2"
                  data-testid={`cp-identity-${g.section}-${r.field}`}
                  style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", overflowWrap: "anywhere" }}
                >
                  {r.value}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      {toggle}
    </div>
  );
}
